/**
 * Invite Accept Page (v0.10.0 - Issue #213)
 * 
 * Public page where users can view and accept club invites.
 * Handles both authenticated and unauthenticated users.
 */

import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { invitesApi } from '../lib/api/invites';
import { useMe } from '../hooks/useMe';
import { useAuthModal } from '../contexts/AuthModalContext';

export default function InviteAcceptPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { data: me, isLoading: meLoading } = useMe();
  const { openLoginModal } = useAuthModal();
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // Fetch invite details
  const {
    data: inviteData,
    isLoading: inviteLoading,
    error: inviteError,
  } = useQuery({
    queryKey: ['invite', code],
    queryFn: () => invitesApi.getInviteDetails(code!),
    enabled: !!code,
    retry: false,
  });

  // Accept invite mutation
  const acceptMutation = useMutation({
    mutationFn: () => invitesApi.acceptInvite(code!),
    onSuccess: (data: { club_id: string; role: string }) => {
      // Redirect to club page
      navigate(`/clubs/${data.club_id}`);
    },
    onError: (error: Error) => {
      setAcceptError(error.message || 'Unable to accept invite');
    },
  });

  const invite = inviteData?.invite;
  const isAuthenticated = !!me && !meLoading;

  // Check if invite is expiring soon (within 7 days)
  const isExpiringSoon = useMemo(() => {
    if (!invite?.expires_at) return false;
    const sevenDaysFromNow = Date.now() + (7 * 24 * 60 * 60 * 1000);
    return invite.expires_at < sevenDaysFromNow;
  }, [invite?.expires_at]);

  // Handle accept button click
  const handleAccept = () => {
    if (!isAuthenticated) {
      // Store invite code and open login modal
      sessionStorage.setItem('pending_invite', code!);
      openLoginModal(`/invite/${code}`);
      return;
    }

    acceptMutation.mutate();
  };

  // Auto-accept if user just logged in with pending invite
  useEffect(() => {
    const pendingInvite = sessionStorage.getItem('pending_invite');
    if (isAuthenticated && pendingInvite === code && invite?.status === 'pending') {
      sessionStorage.removeItem('pending_invite');
      acceptMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, code, invite?.status]);

  if (inviteLoading || meLoading) {
    return (
      <div className="min-h-screen bg-oro-surface-primary flex items-center justify-center">
        <div className="text-center">
          <div className="oro-loading h-12 w-12 rounded-full bg-oro-primary mx-auto mb-4 opacity-80" />
          <p className="text-oro-text-secondary">Loading invite...</p>
        </div>
      </div>
    );
  }

  if (inviteError || !invite) {
    return (
      <div className="min-h-screen bg-oro-surface-primary flex items-center justify-center p-4">
        <div className="bg-oro-surface-secondary rounded-lg shadow-sm p-8 max-w-md w-full text-center">
          <div className="text-oro-error text-5xl mb-4">❌</div>
          <h1 className="text-h2 font-bold text-oro-text-primary mb-2">Invite Not Found</h1>
          <p className="text-oro-text-secondary mb-6">
            This invite link is invalid or has expired.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-oro-primary text-white px-6 py-2 rounded-lg hover:bg-oro-primary-hover transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Show error if invite is not pending
  if (invite.status !== 'pending') {
    const statusMessages = {
      accepted: 'This invite has already been accepted.',
      revoked: 'This invite has been revoked.',
      expired: 'This invite has expired.',
    };

    return (
      <div className="min-h-screen bg-oro-surface-primary flex items-center justify-center p-4">
        <div className="bg-oro-surface-secondary rounded-lg shadow-sm p-8 max-w-md w-full text-center">
          <div className="text-oro-text-tertiary text-5xl mb-4">⚠️</div>
          <h1 className="text-h2 font-bold text-oro-text-primary mb-2">Invite Not Available</h1>
          <p className="text-oro-text-secondary mb-6">
            {statusMessages[invite.status as keyof typeof statusMessages] || 'This invite is no longer valid.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-oro-primary text-white px-6 py-2 rounded-lg hover:bg-oro-primary-hover transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const roleText = invite.type === 'club_leader' ? 'club leader' : 'member';

  return (
    <div className="min-h-screen bg-oro-surface-primary flex items-center justify-center p-4">
      <div className="bg-oro-surface-secondary rounded-lg shadow-sm p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-4">🎟️</div>
          <h1 className="text-h2 font-bold text-oro-text-primary mb-2">You're Invited!</h1>
          <p className="text-oro-text-secondary">
            {invite.inviter.name} has invited you to join
          </p>
        </div>

        {/* Club Info */}
        <div className="bg-oro-surface-tertiary border border-oro-border rounded-lg p-6 mb-6 text-center">
          {invite.club.logo_url && (
            <img
              src={invite.club.logo_url}
              alt={invite.club.name}
              className="w-20 h-20 rounded-full mx-auto mb-3 object-cover"
            />
          )}
          <h2 className="text-h3 font-bold text-oro-text-primary mb-1">{invite.club.name}</h2>
          <p className="text-sm text-oro-text-secondary">as a {roleText}</p>
        </div>

        {/* Role Description */}
        <div className="mb-6">
          {invite.type === 'club_leader' ? (
            <div className="bg-oro-info-bg border border-oro-info rounded-lg p-4">
              <p className="text-sm text-oro-text-primary">
                <strong>As a club leader</strong>, you'll be able to create events, manage members, 
                and help grow your running community.
              </p>
            </div>
          ) : (
            <div className="bg-oro-success-bg border border-oro-success rounded-lg p-4">
              <p className="text-sm text-oro-text-primary">
                <strong>As a member</strong>, you'll be able to RSVP for events, check in at runs, 
                and connect with fellow runners.
              </p>
            </div>
          )}
        </div>

        {/* Expiration Warning */}
        {isExpiringSoon && invite.expires_at && (
          <div className="bg-oro-warning-bg border border-oro-warning rounded-lg p-4 mb-6">
            <p className="text-sm text-oro-text-primary">
              ⚠️ This invite expires{' '}
              {new Date(invite.expires_at).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>
        )}

        {/* Error Message */}
        {acceptError && (
          <div className="bg-oro-error-bg border border-oro-error rounded-lg p-4 mb-6">
            <p className="text-sm text-oro-text-primary">{acceptError}</p>
          </div>
        )}

        {/* Accept Button */}
        <button
          onClick={handleAccept}
          disabled={acceptMutation.isPending}
          className="w-full bg-oro-primary text-white py-3 rounded-lg font-semibold hover:bg-oro-primary-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {acceptMutation.isPending
            ? 'Accepting...'
            : isAuthenticated
            ? 'Accept Invite'
            : 'Sign In to Accept'}
        </button>

        {/* Footer */}
        <p className="text-center text-sm text-oro-text-tertiary mt-6">
          Don't want to join? You can safely ignore this invite.
        </p>
      </div>
    </div>
  );
}
